import * as React from 'react';

import { Flex } from '@lightspeed/flame/Core';
import { Icon } from '@lightspeed/flame/Icon';
import { ModalHeader, ModalBody } from '@lightspeed/flame/Modal';
import { Text, TextLink } from '@lightspeed/flame/Text';

import { errorMap } from 'label/LabelPrintErrors';
import { PRINTER_STATUSES } from 'pages/views/devices/socketClient/types';
import { URL } from 'pages/views/devices/utils/help-urls';

interface Props {
  error?: Error | undefined;
  labelPrinterStatus: PRINTER_STATUSES | undefined;
  includeHeader?: boolean;
}

export enum LabelPrintError {
  ApiError = 'ApiError',
  MissingDataError = 'MissingDataError',
}

type Errors = PRINTER_STATUSES | LabelPrintError;
type ErrorMap = { [key in Errors]?: JSX.Element };

export const HubLabelPrintError: React.FC<Props> = ({
  error,
  labelPrinterStatus,
  includeHeader = true,
}) => {
  const titles: ErrorMap = {
    [PRINTER_STATUSES.DISCONNECTED]: (
      <>
        {includeHeader && <Header>Printer not connected</Header>}
        <Body linkHash="#connect">
          <Text data-test="zebra-disconnected-error">
            The Zebra printer is not connected. To print the selected labels, check that the printer
            is powered on and that the USB cord is properly connected to both the printer and
            computer.
          </Text>
        </Body>
      </>
    ),
    [PRINTER_STATUSES.NOT_RESPONDING]: (
      <>
        {includeHeader && <Header>Printer is connected but not responding</Header>}
        <Body linkHash="#connect">
          <Text data-test="zebra-not-responding-error">
            The Zebra printer is connected but not responding. To print the selected labels, try
            resetting your printer. If the issue persists, try restarting your printer. If none of
            these instructions resolve the issue, contact Support.
          </Text>
        </Body>
      </>
    ),
    [PRINTER_STATUSES.ERROR]: (
      <>
        {includeHeader && <Header>A connection error occurred</Header>}
        <Body>
          <Text mb={2}>
            There is a connection error between Lightspeed Hub and the Zebra printer. Check that
            your printer is connected to the USB cord and that the Hub application is running.
          </Text>
          <Text>If the error persists, contact Support.</Text>
        </Body>
      </>
    ),
    [PRINTER_STATUSES.COVER_OPEN]: (
      <>
        {includeHeader && <Header>Printer is open</Header>}
        <Body linkHash="#open">
          <Text>
            The Zebra printer&apos;s top cover is open. To print the selected labels, check that the
            top cover is completely closed and try again.
          </Text>
        </Body>
      </>
    ),
    [PRINTER_STATUSES.NO_PAPER]: (
      <>
        {includeHeader && <Header>Out of paper</Header>}
        <Body linkHash="#paper">
          <Text>
            The Zebra printer is out of paper. To print the selected labels, load the printer with
            labels and try again.
          </Text>
        </Body>
      </>
    ),
    [LabelPrintError.ApiError]: (
      <>
        {includeHeader && <Header>{errorMap.EMPTY_API_RESPONSE.title}</Header>}
        <Body>
          <Text mb={1} fontWeight="bold">
            {errorMap.EMPTY_API_RESPONSE.body.title}
          </Text>
          <ul>
            <li>{errorMap.EMPTY_API_RESPONSE.body.description}</li>
          </ul>
        </Body>
      </>
    ),
  };

  return titles[getError(error, labelPrinterStatus)] || null;
};

const getError = (
  error: Error | undefined,
  labelPrinterStatus: PRINTER_STATUSES | undefined
): Errors => {
  if (labelPrinterStatus !== PRINTER_STATUSES.READY && labelPrinterStatus !== undefined) {
    return labelPrinterStatus;
  }
  if (error && error.message in LabelPrintError) {
    return error.message as LabelPrintError;
  }
  return PRINTER_STATUSES.ERROR;
};

interface BodyProps {
  children: React.ReactNode;
  linkHash?: string;
}

const Body: React.FC<BodyProps> = ({ children, linkHash }) => (
  <ModalBody>
    <Flex>
      <Flex flexDirection="column" justifyContent="center">
        {children}
        <TextLink
          mt={3}
          style={{ display: 'flex' }}
          href={`${URL.LABEL_TROUBLESHOOTING}${linkHash}`}
          target="_blank"
          data-test="zebra-error-more-info"
        >
          <Text mr={2}>Find more information</Text> <Icon name="external-link" color="secondary" />
        </TextLink>
      </Flex>
    </Flex>
  </ModalBody>
);

const Header: React.FC = props => (
  <ModalHeader data-test="print-label-error-header">{props.children}</ModalHeader>
);
